import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Button, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { getTagsForObject, updateTags } from "../../api/tags";
import { getDisplayName } from "../../utils/privacy";
import { ResizableDrawer } from "../common/ResizableDrawer";
import { useGlobalState } from "../globalState";
import { fetchPersonDetails, PersonDetails } from "../grading/person-details";
import { PageLoader } from "../page-loader";
import { AddToRoleDialog } from "../person/add-to-role-dialog";
import type { ActivityFilter } from "../person/communication-timeline/timeline-types";
import { PersonContent } from "../person/person-content";

/** Stepping contract supplied by the view that owns the list the panel was
 * opened from — the panel renders the arrows, the owner moves the position. */
export interface PersonStepper {
	hasPrevious: boolean;
	hasNext: boolean;
	onPrevious: () => void;
	onNext: () => void;
	/** Called after the Mark-as-Done tag removal so the owner can advance. */
	onMarkedDone: () => void;
}

interface PersonModalProps {
	open: boolean;
	personId: number;
	/** Optional - when provided, used to highlight/scope the person view */
	personAssignmentId?: number;
	onClose: () => void;
	/** When set, shows "Mark as Done" button to remove this tag */
	filterTag?: string;
	/** When set, renders previous/next arrows and binds the arrow keys. */
	stepper?: PersonStepper;
	/** Tab slug to open initially (e.g. "activity", "roles"). Defaults to first tab. */
	initialTab?: string;
	/** Pre-seed the Activity tab filters (e.g. ["notes"] for COMMENT chip click). */
	initialActivityFilters?: ActivityFilter[];
}

export const PersonModal = ({
	open,
	personId,
	personAssignmentId,
	onClose,
	filterTag,
	stepper,
	initialTab,
	initialActivityFilters,
}: PersonModalProps) => {
	const [loading, setLoading] = useState(true);
	const [personDetails, setPersonDetails] = useState<PersonDetails>(null);
	const [markingDone, setMarkingDone] = useState(false);
	const [markedDone, setMarkedDone] = useState(false);
	const [addToRoleOpen, setAddToRoleOpen] = useState(false);
	const [roleAddedCounter, setRoleAddedCounter] = useState(0);
	const [activeTab, setActiveTab] = useState<string | undefined>(initialTab);
	const { currentUser } = useGlobalState();

	// Honour the latest props after the modal is reopened with different args.
	useEffect(() => {
		setActiveTab(initialTab);
	}, [initialTab, personId]);

	const handleMarkAsDone = async () => {
		if (!filterTag) return;
		setMarkingDone(true);
		const currentTags = await getTagsForObject("persons", personId);
		const newTags = currentTags.filter((t: string) => t !== filterTag);
		await updateTags("persons", personId, newTags);
		setMarkingDone(false);
		if (stepper) {
			stepper.onMarkedDone();
		} else {
			setMarkedDone(true);
		}
	};

	const handleClose = () => {
		onClose();
	};

	useHotkeys(
		"left",
		() => {
			if (stepper?.hasPrevious) stepper.onPrevious();
		},
		{ enabled: stepper !== undefined && !addToRoleOpen },
		[stepper, addToRoleOpen]
	);
	useHotkeys(
		"right",
		() => stepper?.onNext(),
		{ enabled: stepper !== undefined && !addToRoleOpen },
		[stepper, addToRoleOpen]
	);

	useEffect(() => {
		let isStale = false;
		setLoading(true);
		setPersonDetails(null);
		setMarkedDone(false);
		fetchPersonDetails(
			personId,
			true,
			undefined,
			currentUser?.userprofile?.show_summary_only || false,
			500,
			true
		).then((personDetails) => {
			if (isStale) return;
			setPersonDetails(personDetails);
			setLoading(false);
		});
		return () => {
			isStale = true;
		};
	}, [personId, currentUser]);

	return (
		<>
			<ResizableDrawer
				open={open}
				onClose={handleClose}
				dataComponent="person-modal"
			>
				{!loading && personDetails ? (
					<Box
						sx={{
							height: "100%",
							padding: (theme) => theme.spacing(2),
						}}
						data-component="person-modal-content"
					>
						<PersonContent
							personId={personId}
							personDetails={personDetails}
							assignmentId={personAssignmentId}
							scrollHeight="calc(100vh - 50px)"
							roleAddedCounter={roleAddedCounter}
							activeTab={activeTab}
							onTabChange={setActiveTab}
							initialActivityFilters={initialActivityFilters}
							headerSlot={
								<Paper
									sx={{ p: 2 }}
									data-component="person-modal-header"
								>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<Typography variant="h6">
												{getDisplayName(
													personDetails.personInfo?.name ||
														personDetails.personSummary
															?.name ||
														"",
													personId,
													currentUser?.userprofile
												)}
											</Typography>
											{stepper && (
												<>
													<IconButton
														size="small"
														onClick={stepper.onPrevious}
														disabled={!stepper.hasPrevious}
														data-component="previous-person-button"
													>
														<NavigateBeforeIcon />
													</IconButton>
													<IconButton
														size="small"
														onClick={stepper.onNext}
														data-component="next-person-button"
													>
														<NavigateNextIcon />
													</IconButton>
												</>
											)}
										</Stack>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
										>
											<Button
												variant="contained"
												color="secondary"
												startIcon={<AssignmentIcon />}
												onClick={() => setAddToRoleOpen(true)}
												data-component="add-to-role-button"
											>
												Add to Board
											</Button>
											{filterTag && !markedDone && (
												<Button
													variant="contained"
													color="success"
													startIcon={<CheckCircleIcon />}
													onClick={handleMarkAsDone}
													disabled={markingDone}
													data-component="mark-done-button"
												>
													{markingDone
														? "Marking..."
														: "Mark as Done"}
												</Button>
											)}
											{markedDone && (
												<Button
													variant="contained"
													color="success"
													startIcon={<CheckCircleIcon />}
													disabled
													data-component="marked-done-button"
												>
													Done!
												</Button>
											)}
											<IconButton
												onClick={handleClose}
												data-component="close-button"
											>
												<CloseIcon />
											</IconButton>
										</Stack>
									</Stack>
								</Paper>
							}
						/>
					</Box>
				) : (
					<PageLoader />
				)}
			</ResizableDrawer>
			<AddToRoleDialog
				personId={personId}
				open={addToRoleOpen}
				onClose={() => setAddToRoleOpen(false)}
				onAdded={() => setRoleAddedCounter((c) => c + 1)}
			/>
		</>
	);
};
